import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useThumbnailSelectionContext } from '../context/ThumbnailSelectionContext';

export interface FileSelection {
  selectedIds: Set<number>;
  selectedCount: number;
  focusedId: number | null;
  isSelected: (id: number) => boolean;
  isFocused: (id: number) => boolean;
  handleSelect: (id: number, shiftKey: boolean) => void;
  clearSelection: () => void;
  selectAll: (totalCount: number) => void;
  setFocusedId: (id: number | null) => void;
  handleKeyDown: (e: KeyboardEvent, totalCount: number, cols: number) => void;
}

export function useFileSelection(sectionId: string): FileSelection {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [focusedId, setFocusedId] = useState<number | null>(null);
  const context = useThumbnailSelectionContext();
  const lastSelectedRef = useRef<number | null>(null);
  const focusedIdRef = useRef<number | null>(null);
  focusedIdRef.current = focusedId;

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setFocusedId(null);
    lastSelectedRef.current = null;
    context?.deactivateSection(sectionId);
  }, [context, sectionId]);

  useEffect(() => {
    const unregister = context?.registerClear(sectionId, () => {
      setSelectedIds(new Set());
      setFocusedId(null);
      lastSelectedRef.current = null;
    });

    return () => {
      unregister?.();
    };
  }, [context, sectionId]);

  const toggleItem = useCallback(
    (id: number) => {
      context?.activateSection(sectionId);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        const wasSelected = next.has(id);
        if (wasSelected) {
          next.delete(id);
          setFocusedId(null);
        } else {
          next.add(id);
          setFocusedId(id);
        }
        if (next.size === 0) {
          context?.deactivateSection(sectionId);
        }
        return next;
      });
      lastSelectedRef.current = id;
    },
    [context, sectionId]
  );

  const rangeSelect = useCallback(
    (id: number) => {
      const anchor = lastSelectedRef.current;
      if (anchor === null) {
        toggleItem(id);
        return;
      }
      context?.activateSection(sectionId);
      const start = Math.min(anchor, id);
      const end = Math.max(anchor, id);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (let i = start; i <= end; i++) {
          next.add(i);
        }
        return next;
      });
      setFocusedId(id);
    },
    [context, sectionId, toggleItem]
  );

  const handleSelect = useCallback(
    (id: number, shiftKey: boolean) => {
      if (shiftKey) {
        rangeSelect(id);
      } else {
        toggleItem(id);
      }
    },
    [toggleItem, rangeSelect]
  );

  const selectAll = useCallback(
    (totalCount: number) => {
      context?.activateSection(sectionId);
      setSelectedIds(new Set(Array.from({ length: totalCount }, (_, i) => i)));
    },
    [context, sectionId]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent, totalCount: number, cols: number) => {
      if (totalCount === 0) return;

      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        selectAll(totalCount);
        return;
      }

      if (e.key === 'Escape') {
        clearSelection();
        return;
      }

      const current = focusedIdRef.current ?? -1;
      let next = current;

      switch (e.key) {
        case 'ArrowRight':
          next = Math.min(current + 1, totalCount - 1);
          break;
        case 'ArrowLeft':
          next = Math.max(current - 1, 0);
          break;
        case 'ArrowDown':
          next = Math.min(current + cols, totalCount - 1);
          break;
        case 'ArrowUp':
          next = Math.max(current - cols, 0);
          break;
        case ' ':
        case 'Enter':
          e.preventDefault();
          if (current >= 0 && current < totalCount) {
            toggleItem(current);
          }
          return;
        default:
          return;
      }

      e.preventDefault();
      setFocusedId(next);

      if (e.shiftKey) {
        context?.activateSection(sectionId);
        setSelectedIds((prev) => {
          const newSet = new Set(prev);
          newSet.add(next);
          return newSet;
        });
      }
    },
    [selectAll, clearSelection, toggleItem, context, sectionId]
  );

  return useMemo(
    () => ({
      selectedIds,
      selectedCount: selectedIds.size,
      focusedId,
      isSelected: (id: number): boolean => selectedIds.has(id),
      isFocused: (id: number): boolean => focusedId === id,
      handleSelect,
      clearSelection,
      selectAll,
      setFocusedId,
      handleKeyDown,
    }),
    [selectedIds, focusedId, handleSelect, clearSelection, selectAll, handleKeyDown]
  );
}
